import React, { useState, useRef, ReactNode, useCallback, useMemo } from 'react';
import { Sizes } from 'enums/sizes';
import {
  PanelContext,
  DialogContext,
  PanelConfig,
  PanelActions,
  DialogConfig,
  DialogActions,
  OverlaySession,
  OverlayType,
  PanelContextValue,
  DialogContextValue,
} from './OverlayContext';
import { PanelRenderer } from './renderers/PanelRenderer';
import { DialogRenderer } from './renderers/DialogRenderer';

function findLastIndex<T>(arr: T[], predicate: (item: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) return i;
  }
  return -1;
}

const isPanel = (s: OverlaySession) => s.type === 'panel';
const isDialog = (s: OverlaySession) => s.type === 'dialog' || s.type === 'blank-dialog';

export const OverlayProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<OverlaySession[]>([]);
  const nextIdRef = useRef(0);

  // ============================================
  // Shared helpers
  // ============================================

  const removeSession = useCallback((id: string) => {
    setSessions(prev => prev.filter(s => s.id !== id));
  }, []);

  const addSession = useCallback((type: OverlayType, config: PanelConfig | DialogConfig, content: JSX.Element) => {
    setSessions(prev => [
      ...prev,
      { id: `${type}-${++nextIdRef.current}`, type, config, content },
    ]);
  }, []);

  const markClosing = useCallback((predicate: (s: OverlaySession) => boolean) => {
    setSessions(prev => {
      const idx = findLastIndex(prev, s => predicate(s) && !s.closing);
      if (idx === -1) return prev;
      const newSessions = [...prev];
      newSessions[idx] = { ...newSessions[idx], closing: true };
      return newSessions;
    });
  }, []);

  const updateActions = useCallback(
    <T extends PanelActions | DialogActions>(predicate: (s: OverlaySession) => boolean, actionsOrUpdater: T | undefined | ((prev: T | undefined) => T | undefined)) => {
      setSessions(prev => {
        const idx = findLastIndex(prev, predicate);
        if (idx === -1) return prev;
        const newSessions = [...prev];
        const session = { ...newSessions[idx] };
        session.actions = typeof actionsOrUpdater === 'function'
          ? (actionsOrUpdater as (prev: T | undefined) => T | undefined)(session.actions as T | undefined)
          : actionsOrUpdater;
        newSessions[idx] = session;
        return newSessions;
      });
    },
    []
  );

  // ============================================
  // Panel API
  // ============================================

  const openPanel = useCallback((config: PanelConfig, content: JSX.Element) => {
    addSession('panel', config, content);
  }, [addSession]);

  const closePanel = useCallback(() => {
    setSessions(prev => {
      const idx = findLastIndex(prev, s => isPanel(s) && !s.closing);
      if (idx === -1) return prev;
      const newSessions = [...prev];
      const session = { ...newSessions[idx] };
      (session.actions as PanelActions)?.onClose?.();
      session.closing = true;
      newSessions[idx] = session;
      return newSessions;
    });
  }, []);

  const setPanelActions = useCallback(
    (actionsOrUpdater: PanelActions | undefined | ((prev: PanelActions | undefined) => PanelActions | undefined)) => {
      updateActions<PanelActions>(isPanel, actionsOrUpdater);
    },
    [updateActions]
  );

  // ============================================
  // Dialog API
  // ============================================

  const openDialog = useCallback((config: DialogConfig, content: JSX.Element) => {
    addSession('dialog', config, content);
  }, [addSession]);

  const openBlankDialog = useCallback((config: DialogConfig, content: JSX.Element) => {
    setSessions(prev => {
      const lastBlankIdx = findLastIndex(prev, s => s.type === 'blank-dialog' && !s.closing);
      if (lastBlankIdx !== -1) {
        const newSessions = [...prev];
        newSessions[lastBlankIdx] = { ...newSessions[lastBlankIdx], config, content };
        return newSessions;
      }
      return [
        ...prev,
        { id: `blank-dialog-${++nextIdRef.current}`, type: 'blank-dialog' as OverlayType, config, content },
      ];
    });
  }, []);

  const closeDialog = useCallback(() => {
    markClosing(isDialog);
  }, [markClosing]);

  const setDialogActions = useCallback(
    (actionsOrUpdater: DialogActions | undefined | ((prev: DialogActions | undefined) => DialogActions | undefined), sessionId?: string) => {
      updateActions<DialogActions>(sessionId ? (s) => s.id === sessionId : isDialog, actionsOrUpdater);
    },
    [updateActions]
  );

  const setDialogTitle = useCallback((title: string, sessionId?: string) => {
    setSessions(prev => {
      const idx = findLastIndex(prev, sessionId ? (s) => s.id === sessionId : (s) => isDialog(s) && !s.closing);
      if (idx === -1) return prev;
      const newSessions = [...prev];
      const session = { ...newSessions[idx] };
      session.config = { ...session.config, title };
      newSessions[idx] = session;
      return newSessions;
    });
  }, []);

  // ============================================
  // Derived state
  // ============================================

  const panelSessions = sessions.filter(isPanel);
  const dialogSessions = sessions.filter(isDialog);
  const activePanels = panelSessions.filter(s => !s.closing);
  const activeDialogs = dialogSessions.filter(s => !s.closing);
  const lastPanel = activePanels[activePanels.length - 1];
  const lastDialog = activeDialogs[activeDialogs.length - 1];

  const panelContextValue = useMemo<PanelContextValue>(() => ({
    isOpen: activePanels.length > 0,
    openPanel,
    closePanel,
    setPanelActions,
    panelConfig: (lastPanel?.config as PanelConfig) ?? { title: '', size: Sizes.md },
    panelContent: lastPanel?.content ?? <></>,
    panelActions: lastPanel?.actions as PanelActions | undefined,
  }), [activePanels, lastPanel, openPanel, closePanel, setPanelActions]);

  const dialogContextValue = useMemo<DialogContextValue>(() => ({
    isOpen: activeDialogs.length > 0,
    isBlank: lastDialog?.type === 'blank-dialog',
    openDialog,
    openBlankDialog,
    closeDialog,
    setDialogActions,
    setDialogTitle,
    dialogConfig: (lastDialog?.config as DialogConfig) ?? { size: Sizes.md },
    dialogContent: lastDialog?.content ?? <></>,
    dialogActions: lastDialog?.actions as DialogActions | undefined,
  }), [activeDialogs, lastDialog, openDialog, openBlankDialog, closeDialog, setDialogActions, setDialogTitle]);

  return (
    <PanelContext.Provider value={panelContextValue}>
      <DialogContext.Provider value={dialogContextValue}>
        {children}
        <PanelRenderer sessions={panelSessions} closePanel={closePanel} onTransitionEnd={removeSession} />
        <DialogRenderer sessions={dialogSessions} closeDialog={closeDialog} onTransitionEnd={removeSession} />
      </DialogContext.Provider>
    </PanelContext.Provider>
  );
};
